import { useCallback, useMemo, useState } from "react";

type Callback = (value: string | null) => void;

type BaseCollection = { value: string };

export type ComboBoxCollectionWithInputValueItem = BaseCollection & {
  isInputValueItem?: boolean;
  isEmptyValue?: boolean;
};

type UseComboBoxInputValueItemProps<Collection extends BaseCollection> = {
  onInputValueSelected: (inputValue: string) => void;
  options: Array<Collection>;
  inputValueItemProps: Omit<Collection, "value">;
  shouldIncludeInputValueItem?: (value: string) => boolean;
  emptyOptionProps?: Omit<Collection, "value">;
};

const useComboBoxInputValueItem = <Collection extends BaseCollection>({
  onInputValueSelected,
  options,
  inputValueItemProps,
  shouldIncludeInputValueItem,
  emptyOptionProps,
}: UseComboBoxInputValueItemProps<Collection>) => {
  const [inputValue, setInputValue] = useState("");

  const onChange = useCallback(
    (formCallback: Callback) => (value: string | null) => {
      if (inputValue && value === inputValue) {
        onInputValueSelected(inputValue.trim());
      }
      return formCallback(value ? value.trim() : value);
    },
    [inputValue, onInputValueSelected]
  );

  const items = useMemo(() => {
    if (shouldIncludeInputValueItem && !shouldIncludeInputValueItem(inputValue)) {
      return options;
    }

    if (!inputValue && options.length === 0) {
      return emptyOptionProps
        ? [{ ...emptyOptionProps, value: inputValue, isEmptyValue: true }]
        : [];
    }

    return [
      ...options,
      ...(!inputValue || options.find(({ value }) => value === inputValue)
        ? []
        : [
            {
              value: inputValue,
              isInputValueItem: true,
              ...inputValueItemProps,
            },
          ]),
    ];
  }, [emptyOptionProps, inputValue, inputValueItemProps, options, shouldIncludeInputValueItem]);

  return {
    comboBoxProps: {
      onInputChange: setInputValue,
      onChange,
      items,
    },
  };
};

export default useComboBoxInputValueItem;
